import type { Profesional } from '../../../api/ops/citas';
import type { AltaProfesional, CambiosProfesional, ProblemaCompromisos, ServicioCatalogo } from '../../../api/ops/servicios';
import { ErrorApi } from '../../../api/problema';
import type { EstadoChip } from '../../../componentes/Chip';
import type { NombreIcono } from '../../../componentes/iconos';
import { COLORES } from '../logica';

/** Si cada profesional usa el horario del negocio o uno propio (null: no se pudo leer). */
export type Horarios = Readonly<Record<string, 'negocio' | 'propio' | null>>;

export const horarioDe = (h: 'negocio' | 'propio' | null | undefined): string => (h === 'propio' ? 'Propio' : h === 'negocio' ? 'Como el negocio' : '');

export const ACCESO: Readonly<Record<Profesional['panel_access'], { texto: string; chip: EstadoChip }>> = {
    none: { texto: 'Sin acceso', chip: 'neutral' },
    invited: { texto: 'Invitación enviada', chip: 'pending' },
    granted: { texto: 'Con acceso', chip: 'confirmed' },
};

export type PestanaProfesional = 'datos' | 'servicios' | 'acceso' | 'horario';

export const PESTANAS_PROFESIONAL: readonly [PestanaProfesional, string, NombreIcono][] = [
    ['datos', 'Datos', 'user'],
    ['servicios', 'Servicios', 'scissors'],
    ['acceso', 'Acceso', 'key-round'],
    ['horario', 'Horario', 'clock'],
];

export const esPestanaProfesional = (x: string | null): x is PestanaProfesional => x === 'datos' || x === 'servicios' || x === 'acceso' || x === 'horario';

/** Los servicios activos que hace (por su id). */
export function serviciosDe(p: Profesional, servicios: readonly ServicioCatalogo[]): ServicioCatalogo[] {
    return servicios.filter((s) => s.status === 'active' && p.service_ids.includes(s.id));
}

export interface FormProfesional {
    nombre: string;
    telefono: string;
    email: string;
    dni: string;
    conDni: boolean;
    color: string;
    enLinea: boolean;
    /** La foto subida (`photo_upload_ref`) y cómo se ve mientras tanto. */
    foto: { ref: string; vista: string; nombre: string } | null;
    servicios: string[];
    calendario: 'inherit' | 'own';
    acceso: boolean;
}

export function formVacio(): FormProfesional {
    return { nombre: '', telefono: '', email: '', dni: '', conDni: false, color: COLORES[5]?.hex ?? '#3D5A80', enLinea: true, foto: null, servicios: [], calendario: 'inherit', acceso: false };
}

export function formDe(p: Profesional): FormProfesional {
    return { nombre: p.display_name, telefono: p.phone ?? '', email: p.email ?? '', dni: '', conDni: false, color: p.color ?? COLORES[5]?.hex ?? '#3D5A80', enLinea: p.online_bookable, foto: null, servicios: [...p.service_ids], calendario: 'inherit', acceso: false };
}

export const emailValido = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

export interface ErroresProfesional {
    nombre?: string;
    telefono?: string;
    email?: string;
    acceso?: string;
    servicios?: boolean;
}

/** Los errores del diseño (`proErrs`); el DNI repetido lo dice la API. */
export function erroresProfesional(f: FormProfesional, nuevo: boolean): ErroresProfesional {
    const e: ErroresProfesional = {};
    if (f.nombre.trim() === '') {
        e.nombre = 'Escribe su nombre y apellidos.';
    }
    if (f.telefono.trim() !== '' && f.telefono.replace(/\D/g, '').length < 9) {
        e.telefono = 'Revisa el teléfono: faltan cifras.';
    }
    if (f.email.trim() !== '' && !emailValido(f.email)) {
        e.email = 'Escribe un email válido.';
    }
    if (nuevo && f.acceso && f.email.trim() === '') {
        e.acceso = 'Para darle acceso, escribe su email arriba.';
    }
    if (nuevo && f.servicios.length === 0) {
        e.servicios = true;
    }
    return e;
}

/** Los datos comunes del alta y del cambio (sin lo vacío). */
export function camposProfesional(f: FormProfesional): CambiosProfesional {
    return {
        display_name: f.nombre.trim(),
        ...(f.telefono.trim() === '' ? {} : { phone: f.telefono.trim() }),
        ...(f.email.trim() === '' ? {} : { email: f.email.trim() }),
        ...(f.conDni && f.dni.trim() !== '' ? { national_id: f.dni.trim().toUpperCase() } : {}),
        ...(f.foto === null ? {} : { photo_upload_ref: f.foto.ref }),
        color: f.color,
        online_bookable: f.enLinea,
    };
}

export function altaProfesional(f: FormProfesional): AltaProfesional {
    return { ...camposProfesional(f), online_bookable: f.enLinea, service_ids: f.servicios, panel_access: f.acceso, calendar: f.calendario };
}

/** Las citas futuras que trae `409 commitments_pending` al desactivar (con su texto y su fecha). */
export function citasPendientes(e: unknown): { total: number; lineas: { texto: string; fecha: string | null }[] } | null {
    if (!(e instanceof ErrorApi) || e.codigo !== 'commitments_pending') {
        return null;
    }
    const p = e.problema as unknown as ProblemaCompromisos;
    const grupos = p.commitments ?? [];
    return {
        total: grupos.reduce((n, c) => n + c.count, 0),
        lineas: grupos.flatMap((c) => (c.items ?? []).map((i) => ({ texto: i.label ?? '', fecha: i.date ?? null }))),
    };
}

/** «a***@lola.es»: el email de su cuenta sin enseñarlo entero. */
export function emailOculto(e: string | undefined): string {
    if (e === undefined || !e.includes('@')) {
        return e ?? '';
    }
    const [u = '', d = ''] = e.split('@');
    return `${u.charAt(0)}***@${d}`;
}

/** PNG, JPEG o WebP de hasta 2 MB (G493); null si vale. */
export function errorFoto(f: File): string | null {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
        return 'Tiene que ser una imagen PNG, JPEG o WebP.';
    }
    if (f.size > 2 * 1024 * 1024) {
        return 'Pesa más de 2 MB. Elige otra más ligera.';
    }
    return null;
}
